import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, GraduationCap, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import type { TutorAnalyticsSummary } from '../../../tutor/types/tutorAnalytics';
import type { AssignmentDashboardStats } from '../hooks/useAssignmentDashboardStats';
import { buildRecentActivities, type ActivityItem } from '../utils/recentActivities';
import {
  DashboardCardSkeleton,
  DashboardEmptyState,
  DashboardInlineError,
} from './dashboardUi';

type Props = {
  tutorAnalytics: TutorAnalyticsSummary | null;
  tutorLoading: boolean;
  tutorError: string | null;
  tutorEnabled: boolean;
  genericTutorSessionLabel: string;
  onRetryTutor?: () => void;
  assignmentStats: AssignmentDashboardStats;
  assignmentLoading: boolean;
  assignmentError: string | null;
  assignmentEnabled: boolean;
  onRetryAssignment?: () => void;
};

function iconForType(type: ActivityItem['type']) {
  switch (type) {
    case 'lesson':
      return GraduationCap;
    case 'homework':
      return FileText;
    case 'tutor':
      return MessageCircle;
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

function labelForType(type: ActivityItem['type'], t: (k: string) => string): string {
  switch (type) {
    case 'lesson':
      return t('dashboard.studentHome.activityLesson');
    case 'homework':
      return t('dashboard.studentHome.activityHomework');
    case 'tutor':
      return t('dashboard.studentHome.activityTutor');
    default: {
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

function formatWhen(d: Date, t: (k: string, opts?: Record<string, unknown>) => string): string {
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return t('dashboard.studentHome.analytics.today');
  if (diffDays === 1) return t('dashboard.studentHome.analytics.yesterday');
  if (diffDays < 7) return t('dashboard.studentHome.analytics.daysAgo', { n: diffDays });
  return d.toLocaleDateString();
}

export function RecentActivityCard({
  tutorAnalytics,
  tutorLoading,
  tutorError,
  tutorEnabled,
  genericTutorSessionLabel,
  onRetryTutor,
  assignmentStats,
  assignmentLoading,
  assignmentError,
  assignmentEnabled,
  onRetryAssignment,
}: Props) {
  const { t } = useTranslation();

  const activities = useMemo(
    () =>
      buildRecentActivities({
        tutorAnalytics,
        assignmentStats,
        genericTutorSessionLabel,
      }),
    [tutorAnalytics, assignmentStats, genericTutorSessionLabel],
  );

  const anySource = tutorEnabled || assignmentEnabled;
  const loading =
    (tutorEnabled && tutorLoading) || (assignmentEnabled && assignmentLoading);
  const hasError =
    (tutorEnabled && tutorError && !tutorAnalytics) ||
    (assignmentEnabled && assignmentError && assignmentStats.total === 0);

  if (!anySource) return null;

  if (loading) {
    return <DashboardCardSkeleton variant="list" />;
  }

  if (hasError && activities.length === 0) {
    return (
      <Card className="border-purple-200/60 dark:border-purple-500/25 mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-900 dark:text-white">
            {t('dashboard.studentHome.recentActivity')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DashboardInlineError
            message={
              tutorError ||
              assignmentError ||
              t('dashboard.studentHome.recentActivityError')
            }
            onRetry={tutorError ? onRetryTutor : onRetryAssignment}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200/60 dark:border-purple-500/25 mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-slate-900 dark:text-white">
          {t('dashboard.studentHome.recentActivity')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <DashboardEmptyState>
            {t('dashboard.studentHome.recentActivityEmpty')}
          </DashboardEmptyState>
        ) : (
          <ul className="space-y-0 divide-y divide-slate-100 dark:divide-slate-700/80">
            {activities.map((item) => {
              const Icon = iconForType(item.type);
              return (
                <li key={item.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                    <Icon className="h-4 w-4" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {labelForType(item.type, t)}
                    </p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{item.title}</p>
                  </div>
                  <time
                    className="shrink-0 text-xs text-slate-500 dark:text-slate-400"
                    dateTime={item.at.toISOString()}
                  >
                    {formatWhen(item.at, t)}
                  </time>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
